import { isNil } from 'lodash';

import { DataWindow, equalsShallow } from '@/utils';

/**
 * Every spotter's series, and the window each was assembled from, held behind a class instance.
 *
 * react-query runs a combined result through `replaceEqualDeep` once per observer, which walks every
 * plain array and object it reaches and hands that observer its own copy of whatever changed. Over a
 * series of tens of thousands of rows that is milliseconds of walking per section per new datapoint, and
 * one copy of the series per section. Anything that is not a plain array or object is passed through
 * untouched, so holding the series here is what keeps both out of the live path.
 */
export class AssembledSeries<TItem> {
    /**
     * Makes this type nominal, so it describes the class and not the shape. A structural match would
     * accept a `{ data, windows }` literal where an instance is expected, and `replaceEqualDeep` walks a
     * plain literal instead of passing it through.
     */
    private readonly nominal?: never;

    constructor(
        readonly data: (TItem[] | undefined)[],
        readonly windows: (DataWindow<TItem> | undefined)[]
    ) {}
}

const sameWindow = <TItem>(held: DataWindow<TItem> | undefined, next: DataWindow<TItem> | undefined) =>
    held === next ||
    (!isNil(held) &&
        !isNil(next) &&
        held.data === next.data &&
        held.startMs === next.startMs &&
        held.endMs === next.endMs);

/**
 * The series to hand out for `data` and `windows`, which is `held` itself while every array behind it is
 * unchanged.
 *
 * Reusing the instance is what lets an observer with nothing new compare equal and skip its re-render.
 */
export const nextAssembledSeries = <TItem>(
    held: AssembledSeries<TItem> | undefined,
    data: (TItem[] | undefined)[],
    windows: (DataWindow<TItem> | undefined)[]
): AssembledSeries<TItem> => {
    const isUnchanged =
        !isNil(held) &&
        equalsShallow(held.data, data) &&
        held.windows.length === windows.length &&
        held.windows.every((window, index) => sameWindow(window, windows[index]));

    return isUnchanged ? held : new AssembledSeries(data, windows);
};
